import { useEffect, useRef } from 'react'
import { TASK_STATUS } from '@ovrsee/shared'
import { Countdown } from '../../components/Countdown.js'
import { PriorityDot } from '../../components/PriorityDot.js'
import { StatusChip } from '../../components/StatusChip.js'
import type { MyTask } from './useMyTasks.js'
import { Button, Heading, Text } from '../../ui/index.js'

export function TaskCard({
  task,
  onSubmit,
  onBlock,
  onStart,
  highlight = false,
}: {
  task: MyTask
  onSubmit: () => void
  onBlock: () => void
  onStart: () => void
  highlight?: boolean
}) {
  const ref = useRef<HTMLElement | null>(null)

  useEffect(() => {
    // jsdom has no scrollIntoView; the optional call keeps tests honest.
    if (highlight) ref.current?.scrollIntoView?.({ block: 'center' })
  }, [highlight])

  const latestSubmission = task.submissions?.[0]
  const canStart = task.status === TASK_STATUS.ASSIGNED
  const canSubmit = task.status === TASK_STATUS.IN_PROGRESS || task.status === TASK_STATUS.RETURNED
  const canBlock = task.status === TASK_STATUS.ASSIGNED || canSubmit
  // The API refuses the start; this only mirrors it, so the employee is not
  // clicking a button that always fails (spec §2.4).
  const waitingOn = task.dependsOn != null && task.dependsOn.status !== 'APPROVED' ? task.dependsOn : null

  return (
    <article
      ref={ref}
      aria-current={highlight ? 'true' : undefined}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--o-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--o-space-4)',
        ...(highlight ? { outline: '2px solid var(--mauve-200)', outlineOffset: 2 } : {}),
      }}
    >
      <div className="row-between" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--o-space-2)' }}>
            <PriorityDot priority={task.priority} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-muted)' }}>
              {task.reference}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 4 }}>{task.category}</div>
          <Heading type="h3">{task.title}</Heading>
        </div>
        <StatusChip status={task.status} isOverdue={task.isOverdue} />
      </div>

      {task.description !== undefined && task.description !== '' ? (
        <Text color="secondary">{task.description}</Text>
      ) : null}

      <div>
        <div className="card-label">DUE IN</div>
        {task.status !== 'SUBMITTED' && task.status !== 'APPROVED' && task.assignedAt !== undefined ? (
          <Countdown
            deadlineIso={task.effectiveDeadlineAt}
            assignedAtIso={task.assignedAt}
            expiredLabel={task.dueIn === '—' ? 'Overdue' : `Overdue by ${task.dueIn}`}
            live
          />
        ) : (
          <span className="mono tnum" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            {task.dueIn}
          </span>
        )}
      </div>

      {task.status === TASK_STATUS.RETURNED && latestSubmission?.reviewNote !== undefined ? (
        <div
          style={{
            background: 'var(--brand-soft)',
            border: '1px solid var(--brand)',
            borderRadius: 'var(--r-md)',
            padding: 'var(--o-space-3) var(--o-space-4)',
            color: 'var(--fg-2)',
            fontSize: 13,
          }}
        >
          {latestSubmission.reviewNote}
        </div>
      ) : null}

      {waitingOn !== null ? (
        <div style={{ fontSize: 12.5, color: 'var(--warning)' }}>
          Waiting on {waitingOn.reference} · {waitingOn.title}
        </div>
      ) : null}

      {canStart || canSubmit || canBlock ? (
        <div className="row-actions" style={{ flexWrap: 'wrap' }}>
          {canStart ? (
            <Button kind="primary" size="small" onClick={onStart} disabled={waitingOn !== null}>
              Start
            </Button>
          ) : null}
          {canSubmit ? (
            <Button kind="secondary" size="small" onClick={onSubmit}>
              Submit
            </Button>
          ) : null}
          {canBlock ? (
            <Button kind="secondary" size="small" onClick={onBlock}>
              Blocked
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}
